/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
/**
 * Component that creates a 3D Modals
 * required params:
 *  - position
 *  - width
 *  - height

 *
 */

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import BackgroundPanel from "./BackgroundPanel";
import OverlayPanel from "./OverlayPanel";
import { Vector3 } from "three";

function Modal({
    id,
    openedModal,
    onOpen,
    position,
    width,
    height,
    title,
    openedPosition,
    ClosedComponent,
    OpenedComponent,
    openedPanelDimension,
    overlay,
    aframeClasses,
    ...otherAttributes
}) {
    const [isOpened, setIsOpened] = useState(openedModal === id);
    const [worldPosition, setWorldPosition] = useState({ x: 0, y: 0, z: 0 });
    const root = useRef();

    useEffect(() => {
        setIsOpened(openedModal === id);
    }, [openedModal]);

    useEffect(() => {
        if (isOpened) {
            const parent = root.current.parentNode;
            const openedPositionParsed = openedPosition.split(" ");
            const worldPos = new Vector3(
                openedPositionParsed[0],
                openedPositionParsed[1],
                openedPositionParsed[2]
            );
            const localPos = parent.object3D.worldToLocal(worldPos);
            if (
                localPos.x !== worldPosition.x ||
                localPos.y !== worldPosition.y ||
                localPos.z !== worldPosition.z
            )
                setWorldPosition(localPos);
        }
    }, [isOpened, worldPosition, openedPosition]);

    const pos = position.split(" ");

    return (
        <a-entity class="Modal">
            <BackgroundPanel
                ref={root}
                class={aframeClasses}
                position={position}
                width={width}
                height={height}
                title={title}
                textOffset={{ x: width / 20, y: width / 20 }}
                animation__close_rotation={`property: rotation; autoplay: true; to: 0 0 0; dur:150; enabled: ${!isOpened}`}
                animation__close_scale-width={`property: width; autoplay: true; to: ${width}; dur:150; enabled: ${!isOpened}`}
                animation__close_scale-height={`property: height; autoplay: true; to: ${height}; dur:150; enabled: ${!isOpened}`}
                animation__close_position={`property: position; autoplay: true;  to: ${
                    pos[0]
                } ${pos[1]} ${pos[2]}; dur:150; enabled: ${!isOpened}`}
                animation__open_rotation={`property: rotation; autoplay: true; to: 0 ${
                    overlay ? -180 : pos[0] < 0 ? -210 : -150
                } 0; dur:150; enabled: ${!!isOpened}`}
                animation__open_position={`property: position; autoplay: true; to: ${
                    worldPosition.x
                } ${worldPosition.y} ${
                    worldPosition.z
                }; dur:150; enabled: ${!!isOpened}`}
                animation__open_scale-width={`property: width; autoplay: true; to: ${openedPanelDimension}; dur:150; enabled: ${!!isOpened}`}
                animation__open_scale-height={`property: height; autoplay: true; to: ${openedPanelDimension}; dur:150; enabled: ${!!isOpened}`}
                {...otherAttributes}
            >
                {isOpened ? (
                    <a-entity rotation="0 180 0">
                        <OpenedComponent
                            openedPanelDimension={openedPanelDimension}
                            onClose={() => {
                                onOpen(false);
                            }}
                        />
                    </a-entity>
                ) : (
                    <ClosedComponent
                        width={width}
                        height={height}
                        onOpen={() => {
                            onOpen(id);
                        }}
                    />
                )}
            </BackgroundPanel>
            {isOpened && overlay && (
                <OverlayPanel
                    aframeClasses={aframeClasses}
                    position={`0 0 ${worldPosition.z * 0.8}`}
                    width={openedPanelDimension * 10}
                    height={openedPanelDimension * 10}
                    onClickHandler={() => {
                        onOpen(false);
                    }}
                />
            )}
        </a-entity>
    );
}

// requiring data
Modal.propTypes = {
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    position: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    openedPosition: PropTypes.string.isRequired,
    ClosedComponent: PropTypes.func.isRequired,
    OpenedComponent: PropTypes.func.isRequired
};

export default Modal;
